import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  DataCollectionDashboardResponse,
  DataCollectionRequestedType,
  DataCollectionRequestRow,
  DataCollectionStatus,
  DataCollectionTargetScope,
} from '../../types';

const CHAIN_OPTIONS = [
  { id: 24, label: 'Village' },
  { id: 26, label: 'Waitrose' },
  { id: 33, label: 'Asda' },
  { id: 30, label: 'Morrisons' },
  { id: 41, label: 'Food Parade' },
  { id: 44, label: 'Collins Family Markets' },
  { id: 46, label: 'Lidl GB' },
  { id: 50, label: 'Lidl FR' },
] as const;

const SCOPE_LABELS: Record<DataCollectionTargetScope, string> = {
  item_number: 'Item',
  normalized_barcode: 'Barcode',
  design: 'Design',
};

const TYPE_LABELS: Record<DataCollectionRequestedType, string> = {
  product_image: 'Product image',
  crops: 'Crops / scans',
};

function parseApiTimestampMs(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return NaN;
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  return new Date(hasTimezone ? trimmed : `${trimmed}Z`).getTime();
}

function formatAge(value: string): string {
  const ageMs = Date.now() - parseApiTimestampMs(value);
  if (!Number.isFinite(ageMs) || ageMs < 0) return '-';
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTarget(row: DataCollectionRequestRow): string {
  if (row.itemNumber) return row.itemNumber;
  if (row.targetScope === 'normalized_barcode') return row.normalizedBarcode || row.targetKey;
  if (row.targetScope === 'design') return row.designId || row.targetKey;
  return row.targetKey;
}

function normalizeMasterLabelUrl(row: DataCollectionRequestRow): string {
  try {
    const url = new URL(row.masterLabelUrl || '/forge/admin/master-label', window.location.origin);
    url.searchParams.set('chain', String(row.chainId));
    return `${url.pathname}${url.search}`;
  } catch {
    return `/forge/admin/master-label?chain=${row.chainId}`;
  }
}

function getRowRequestIds(row: DataCollectionRequestRow): string[] {
  return row.requestIds?.length ? row.requestIds : [row.requestId];
}

function formatScopes(row: DataCollectionRequestRow): string {
  const scopes = row.targetScopes?.length ? row.targetScopes : [row.targetScope];
  return scopes.map((scope) => SCOPE_LABELS[scope] || scope).join(', ');
}

function useIsPhoneView(): boolean {
  const getIsPhone = () => (typeof window === 'undefined' ? false : window.innerWidth <= 760);
  const [isPhone, setIsPhone] = useState(getIsPhone);
  useEffect(() => {
    const handleResize = () => setIsPhone(getIsPhone());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isPhone;
}

const headerCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--divider)',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'middle',
};

const previewCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  width: 118,
};

const selectStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 30,
  padding: '0 9px',
  fontSize: 12,
  fontWeight: 700,
};

const actionButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: '1px solid #2ea043',
  background: 'rgba(46,160,67,0.12)',
  color: '#7ee787',
  borderRadius: 7,
  padding: '7px 10px',
  fontWeight: 800,
  fontSize: 11,
  textDecoration: 'none',
};

export const DataCollectionDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<DataCollectionDashboardResponse | null>(null);
  const [status, setStatus] = useState<DataCollectionStatus | 'all'>('open');
  const [chainId, setChainId] = useState<'all' | number>('all');
  const [scope, setScope] = useState<DataCollectionTargetScope | 'all'>('all');
  const [requestedType, setRequestedType] = useState<DataCollectionRequestedType | 'all'>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [queueIndex, setQueueIndex] = useState(0);
  const isPhoneView = useIsPhoneView();

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ForgeApiService.fetchDataCollectionDashboard(token, {
        status,
        chainId: chainId === 'all' ? null : chainId,
        targetScope: scope,
        requestedType,
        groupBy: 'item_number',
      });
      setDashboard(response);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to load data collection');
    } finally {
      setIsLoading(false);
    }
  }, [chainId, requestedType, scope, showErrorToast, status, token]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const chainOptions = useMemo(() => {
    const fromStats =
      dashboard?.chainStats.map((stat) => Number(stat.key)).filter(Number.isFinite) ?? [];
    return [...new Set([...CHAIN_OPTIONS.map((chain) => chain.id), ...fromStats])]
      .sort((left, right) => left - right)
      .map((id) => ({
        id,
        label:
          dashboard?.chainStats.find((stat) => Number(stat.key) === id)?.label ||
          CHAIN_OPTIONS.find((chain) => chain.id === id)?.label ||
          `Chain ${id}`,
      }));
  }, [dashboard?.chainStats]);

  const rows = dashboard?.rows ?? [];
  const activeQueueRow = rows[Math.min(queueIndex, Math.max(rows.length - 1, 0))] ?? null;

  useEffect(() => {
    setQueueIndex((previous) => Math.min(previous, Math.max(rows.length - 1, 0)));
  }, [rows.length]);

  const handleComplete = async (row: DataCollectionRequestRow) => {
    const requestIds = getRowRequestIds(row);
    setCompletingId(row.requestId);
    try {
      await ForgeApiService.completeDataCollectionRequestsBatch(token, requestIds);
      showSuccessToast(
        requestIds.length > 1 ? 'Data collection requests finished' : 'Data collection request finished'
      );
      await loadDashboard();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to finish request');
    } finally {
      setCompletingId(null);
    }
  };

  const goPrevious = () => setQueueIndex((previous) => Math.max(0, previous - 1));
  const goNext = () => setQueueIndex((previous) => Math.min(Math.max(rows.length - 1, 0), previous + 1));

  return (
    <div className="page-content" style={{ padding: isPhoneView ? 10 : 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--text-primary)', fontSize: isPhoneView ? 20 : 22 }}>
            Data Collection
          </h1>
          <p style={{ margin: '6px 0 0', color: 'var(--text-tertiary)', fontSize: 12 }}>
            Open Forge requests for product images and crops / scans.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadDashboard()}
          disabled={isLoading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            height: 34,
            padding: '0 12px',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text-secondary)',
            borderRadius: 7,
            cursor: isLoading ? 'wait' : 'pointer',
            fontWeight: 800,
          }}
        >
          <RefreshOutlinedIcon style={{ fontSize: 17 }} />
          Refresh
        </button>
      </div>

      {!isPhoneView ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
            gap: 10,
            marginBottom: 14,
          }}
        >
          <StatCard label="Open" value={dashboard?.totalOpen ?? 0} tone="#7ee787" />
          <StatCard label="Completed" value={dashboard?.totalCompleted ?? 0} tone="#58a6ff" />
          <StatCard label="Chains" value={dashboard?.chainStats.length ?? 0} tone="#f2cc46" />
          <StatCard label="Visible rows" value={rows.length} tone="#d2a8ff" />
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as DataCollectionStatus | 'all')}
          style={selectStyle}
        >
          <option value="open">Open</option>
          <option value="completed">Completed</option>
          <option value="all">All</option>
        </select>
        <select
          value={chainId}
          onChange={(event) =>
            setChainId(event.target.value === 'all' ? 'all' : Number(event.target.value))
          }
          style={selectStyle}
        >
          <option value="all">All chains</option>
          {chainOptions.map((chain) => (
            <option key={chain.id} value={chain.id}>
              {chain.label}
            </option>
          ))}
        </select>
        <select
          value={scope}
          onChange={(event) => setScope(event.target.value as DataCollectionTargetScope | 'all')}
          style={selectStyle}
        >
          <option value="all">All targets</option>
          <option value="item_number">Item number</option>
          <option value="normalized_barcode">Normalized barcode</option>
          <option value="design">Design</option>
        </select>
        <select
          value={requestedType}
          onChange={(event) =>
            setRequestedType(event.target.value as DataCollectionRequestedType | 'all')
          }
          style={selectStyle}
        >
          <option value="all">All needs</option>
          <option value="product_image">Product image</option>
          <option value="crops">Crops / scans</option>
        </select>
      </div>

      {isPhoneView ? (
        <PhoneQueueView
          row={activeQueueRow}
          rowCount={rows.length}
          queueIndex={Math.min(queueIndex, Math.max(rows.length - 1, 0))}
          isLoading={isLoading}
          completingId={completingId}
          onPrevious={goPrevious}
          onNext={goNext}
          onComplete={handleComplete}
        />
      ) : (
        <DesktopTable
          rows={rows}
          isLoading={isLoading}
          completingId={completingId}
          onComplete={handleComplete}
        />
      )}
    </div>
  );
};

interface DesktopTableProps {
  rows: DataCollectionRequestRow[];
  isLoading: boolean;
  completingId: string | null;
  onComplete: (row: DataCollectionRequestRow) => void;
}

const DesktopTable: React.FC<DesktopTableProps> = ({
  rows,
  isLoading,
  completingId,
  onComplete,
}) => (
  <div
    style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      overflow: 'hidden',
      background: 'var(--surface)',
    }}
  >
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={headerCellStyle}>Preview</th>
          <th style={headerCellStyle}>Chain</th>
          <th style={headerCellStyle}>Item</th>
          <th style={headerCellStyle}>Product</th>
          <th style={headerCellStyle}>Needs</th>
          <th style={headerCellStyle}>Requested</th>
          <th style={headerCellStyle}>Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.requestId}>
            <td style={previewCellStyle}>
              {row.previewVizUrl ? (
                <img
                  src={row.previewVizUrl}
                  alt=""
                  style={{
                    width: 88,
                    height: 88,
                    objectFit: 'contain',
                    background: '#050505',
                    borderRadius: 8,
                  }}
                />
              ) : (
                <span style={{ color: 'var(--text-tertiary)' }}>-</span>
              )}
            </td>
            <td style={bodyCellStyle}>{row.chainName || `Chain ${row.chainId}`}</td>
            <td style={{ ...bodyCellStyle, fontFamily: 'var(--font-mono)' }}>
              <Link
                to={normalizeMasterLabelUrl(row)}
                style={{ color: '#3fb950', fontWeight: 800, textDecoration: 'none' }}
              >
                {formatTarget(row)}
              </Link>
              <div
                style={{
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 10,
                  marginTop: 3,
                }}
              >
                {formatScopes(row)}
                {(row.requestCount ?? 1) > 1 ? ` · ${row.requestCount} requests` : ''}
              </div>
            </td>
            <td style={bodyCellStyle}>{row.productName || '-'}</td>
            <td style={bodyCellStyle}>
              <NeedChips row={row} />
            </td>
            <td style={bodyCellStyle}>
              <div>{formatAge(row.requestedAt)}</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
                {row.requestedBy || '-'}
              </div>
            </td>
            <td style={bodyCellStyle}>
              <FinishButton row={row} completingId={completingId} onComplete={onComplete} />
            </td>
          </tr>
        ))}
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={7}
              style={{ ...bodyCellStyle, textAlign: 'center', color: 'var(--text-tertiary)' }}
            >
              {isLoading ? 'Loading...' : 'No data collection requests'}
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  </div>
);

interface PhoneQueueViewProps {
  row: DataCollectionRequestRow | null;
  rowCount: number;
  queueIndex: number;
  isLoading: boolean;
  completingId: string | null;
  onPrevious: () => void;
  onNext: () => void;
  onComplete: (row: DataCollectionRequestRow) => void;
}

const PhoneQueueView: React.FC<PhoneQueueViewProps> = ({
  row,
  rowCount,
  queueIndex,
  isLoading,
  completingId,
  onPrevious,
  onNext,
  onComplete,
}) => {
  if (!row) {
    return (
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface)',
          color: 'var(--text-tertiary)',
          padding: 22,
          textAlign: 'center',
        }}
      >
        {isLoading ? 'Loading...' : 'No data collection requests'}
      </div>
    );
  }

  const masterLabelUrl = normalizeMasterLabelUrl(row);
  const canMoveBack = queueIndex > 0;
  const canMoveNext = queueIndex < rowCount - 1;

  return (
    <div style={{ paddingBottom: 88 }}>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            aspectRatio: '1 / 1',
            background: '#050505',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {row.previewVizUrl ? (
            <img
              src={row.previewVizUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ color: 'var(--text-tertiary)', fontWeight: 800 }}>No preview</span>
          )}
        </div>
        <div style={{ padding: 14 }}>
          <div
            style={{
              color: 'var(--text-tertiary)',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginBottom: 5,
            }}
          >
            {queueIndex + 1} / {rowCount} · {row.chainName || `Chain ${row.chainId}`}
          </div>
          <div style={{ color: 'var(--text-primary)', fontSize: 22, fontWeight: 900 }}>
            {row.productName || 'Unknown product'}
          </div>
          <Link
            to={masterLabelUrl}
            style={{
              display: 'inline-block',
              color: '#3fb950',
              fontFamily: 'var(--font-mono)',
              fontSize: 18,
              fontWeight: 900,
              marginTop: 8,
              textDecoration: 'none',
            }}
          >
            {formatTarget(row)}
          </Link>
          <div style={{ marginTop: 12 }}>
            <NeedChips row={row} />
          </div>
          <DetailList row={row} />
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          padding: 10,
          background: 'rgba(15,15,15,0.96)',
          borderTop: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          onClick={onPrevious}
          disabled={!canMoveBack}
          style={{
            ...queueButtonStyle,
            opacity: canMoveBack ? 1 : 0.45,
            cursor: canMoveBack ? 'pointer' : 'not-allowed',
          }}
        >
          <ArrowBackOutlinedIcon style={{ fontSize: 17 }} />
          Previous
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canMoveNext}
          style={{
            ...queueButtonStyle,
            opacity: canMoveNext ? 1 : 0.45,
            cursor: canMoveNext ? 'pointer' : 'not-allowed',
          }}
        >
          Skip
          <ArrowForwardOutlinedIcon style={{ fontSize: 17 }} />
        </button>
        <Link to={masterLabelUrl} style={{ ...queueButtonStyle, textDecoration: 'none' }}>
          <OpenInNewOutlinedIcon style={{ fontSize: 17 }} />
          Open
        </Link>
        <FinishButton
          row={row}
          completingId={completingId}
          onComplete={onComplete}
          style={{ width: '100%', height: 42 }}
        />
      </div>
    </div>
  );
};

const NeedChips: React.FC<{ row: DataCollectionRequestRow }> = ({ row }) => (
  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
    {row.requestedTypes.map((type) => (
      <span
        key={type}
        style={{
          border: '1px solid var(--border)',
          borderRadius: 999,
          padding: '3px 7px',
          color: 'var(--text-primary)',
          fontSize: 10,
          fontWeight: 800,
        }}
      >
        {TYPE_LABELS[type] || type}
      </span>
    ))}
  </div>
);

const DetailList: React.FC<{ row: DataCollectionRequestRow }> = ({ row }) => {
  const normalizedBarcodes = row.normalizedBarcodes?.length
    ? row.normalizedBarcodes
    : row.normalizedBarcode
      ? [row.normalizedBarcode]
      : [];
  const designIds = row.designIds?.length ? row.designIds : row.designId ? [row.designId] : [];
  return (
    <div
      style={{
        display: 'grid',
        gap: 8,
        marginTop: 16,
        color: 'var(--text-secondary)',
        fontSize: 13,
      }}
    >
      <DetailRow label="Requested" value={`${formatAge(row.requestedAt)} · ${row.requestedBy || '-'}`} />
      <DetailRow label="Target" value={formatScopes(row)} />
      <DetailRow label="Requests" value={String(row.requestCount ?? 1)} />
      {normalizedBarcodes.length ? (
        <DetailRow label="Barcodes" value={normalizedBarcodes.join(', ')} mono />
      ) : null}
      {designIds.length ? <DetailRow label="Designs" value={designIds.join(', ')} mono /> : null}
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 8 }}>
    <div style={{ color: 'var(--text-tertiary)', fontWeight: 800 }}>{label}</div>
    <div style={{ fontFamily: mono ? 'var(--font-mono)' : undefined, wordBreak: 'break-word' }}>
      {value}
    </div>
  </div>
);

const FinishButton: React.FC<{
  row: DataCollectionRequestRow;
  completingId: string | null;
  onComplete: (row: DataCollectionRequestRow) => void;
  style?: React.CSSProperties;
}> = ({ row, completingId, onComplete, style }) => {
  if (row.status !== 'open') {
    return (
      <span style={{ color: 'var(--text-tertiary)' }}>
        {row.completedBy ? `by ${row.completedBy}` : 'Completed'}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onComplete(row)}
      disabled={completingId === row.requestId}
      style={{
        ...actionButtonStyle,
        ...style,
        cursor: completingId === row.requestId ? 'wait' : 'pointer',
      }}
    >
      <CheckCircleOutlineOutlinedIcon style={{ fontSize: 16 }} />
      Finished
    </button>
  );
};

const queueButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: 42,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 7,
  fontWeight: 900,
  fontSize: 12,
};

const StatCard: React.FC<{ label: string; value: number; tone: string }> = ({
  label,
  value,
  tone,
}) => (
  <div
    style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      background: 'var(--surface)',
      padding: 12,
    }}
  >
    <div
      style={{
        color: 'var(--text-tertiary)',
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
    <div
      style={{
        color: tone,
        fontFamily: 'var(--font-mono)',
        fontSize: 22,
        fontWeight: 900,
        marginTop: 5,
      }}
    >
      {value.toLocaleString()}
    </div>
  </div>
);
